import { FontPreference } from "@App/whiteLabelReducer";
import { SYSTEM_FONTS } from "@App/constants/appConstants";

// Precompute once to avoid rebuilding on each call
const SYSTEM_FONT_SET = new Set((SYSTEM_FONTS || []).map((f: any) => String(f?.value || "")));

interface WhitelabelAPIResponse {
  config?: {
    font_preference?: FontPreference;
  };
}

// Basic classifier: treat "Default" and common system stacks as non-Google
export const isSystemFont = (fontFamily?: string): boolean => {
  if (!fontFamily) return true;
  const trimmedName = fontFamily.trim();
  if (trimmedName === "Default") return true;
  return SYSTEM_FONT_SET.has(trimmedName);
};

export const buildGoogleFontUrl = (fontFamily?: string): string | null => {
  if (!fontFamily || isSystemFont(fontFamily)) return null;
  return `https://fonts.googleapis.com/css2?family=${fontFamily.replace(
    / /g,
    "+",
  )}:wght@300;400;500;600;700&display=swap`;
};

export const buildGoogleFontUrls = (pref?: FontPreference): string[] => {
  if (!pref) return [];
  if (pref.mode === "single") {
    const u = buildGoogleFontUrl(pref.single_font);
    return u ? [u] : [];
  }
  const urls: string[] = [];
  const titleFontUrl = buildGoogleFontUrl(pref.title_font);
  const bodyFontUrl = buildGoogleFontUrl(pref.body_font);
  if (titleFontUrl) urls.push(titleFontUrl);
  if (bodyFontUrl && bodyFontUrl !== titleFontUrl) urls.push(bodyFontUrl);
  return urls;
};

// Processes fonts from API response and dynamically loads them if needed.
export const processFonts = (
  data: WhitelabelAPIResponse,
): { fonts: FontPreference; font_urls: string[] } => {
  const fontConfig = data?.config?.font_preference;

  // Fallback if missing config
  if (!fontConfig) {
    return {
      fonts: {
        mode: "single",
        single_font: "Default",
        title_font: "Default",
        body_font: "Default",
      },
      font_urls: [],
    };
  }
  const urls = buildGoogleFontUrls(fontConfig);
  return { fonts: fontConfig, font_urls: urls };
};
